import React, { useState, useMemo } from 'react';
import { useBetting } from '../../context/BettingContext';
import { MARKET_TYPES, COMPETITIONS } from '../../data/mockData';
import './MyBets.css';

function BetRow({ bet, market, onEdit, onCancel, canModify, cancelling }) {
  const typeInfo = market ? MARKET_TYPES[Object.keys(MARKET_TYPES).find(
    (k) => MARKET_TYPES[k].id === market.type
  )] : null;

  const comp = market ? COMPETITIONS[Object.keys(COMPETITIONS).find(
    (k) => COMPETITIONS[k].id === market.competition
  )] : null;

  const statusClass = { pending: 'mybets-status--pending', won: 'mybets-status--won', lost: 'mybets-status--lost' }[bet.status] || '';
  const statusLabel = { pending: 'Active', won: 'Won', lost: 'Lost' }[bet.status] || bet.status;

  return (
    <div className={`mybets-row ${statusClass}`}>
      <div className="mybets-row-left">
        <span className="mybets-row-icon">{typeInfo?.icon || '⚽'}</span>
        <div className="mybets-row-info">
          <div className="mybets-row-match">{market?.matchName || 'Unknown market'}</div>
          <div className="mybets-row-meta">
            <span className="mybets-row-comp" style={{ color: comp?.color }}>{comp?.emoji} {comp?.name}</span>
            <span className="mybets-row-type">{typeInfo?.name}</span>
          </div>
        </div>
      </div>
      <div className="mybets-row-pick">
        <span className="mybets-row-pick-label">Pick</span>
        <span className="mybets-row-pick-value">{bet.optionName}</span>
      </div>
      <div className="mybets-row-stake">
        <span className="mybets-row-stake-label">Stake</span>
        <span className="mybets-row-stake-value">{bet.amount}</span>
      </div>
      <div className="mybets-row-odds">
        <span className="mybets-row-odds-label">Odds</span>
        <span className="mybets-row-odds-value">{bet.odds || (bet.potentialWin / bet.amount).toFixed(2)}</span>
      </div>
      <div className="mybets-row-potential">
        <span className="mybets-row-potential-label">{bet.status === 'won' ? 'Won' : 'Potential'}</span>
        <span className="mybets-row-potential-value">{bet.status === 'lost' ? '-' : `${bet.potentialWin.toLocaleString()}`}</span>
      </div>
      <div className="mybets-row-status">
        <span className={`mybets-status-badge ${statusClass}`}>{statusLabel}</span>
      </div>
      {bet.status === 'pending' && canModify && market?.status === 'open' && (
        <div className="mybets-row-actions">
          <button className="mybets-edit-btn" onClick={() => onEdit(bet, market)}>Change</button>
          <button className="mybets-cancel-btn" onClick={() => onCancel(bet.id)} disabled={cancelling === bet.id}>
            {cancelling === bet.id ? '...' : 'Cancel'}
          </button>
        </div>
      )}
      {bet.status === 'pending' && market?.status === 'closed' && <div className="mybets-row-locked">🔒</div>}
      {bet.replacedAt && (
        <div className="mybets-row-replaced">
          Updated {new Date(bet.replacedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </div>
      )}
    </div>
  );
}

export default function MyBets() {

  const { events, rounds, bets, markets, settings, cancelBet, replaceBet, isMyBet } = useBetting();
  const [filter, setFilter] = useState('all');
  const [cancelling, setCancelling] = useState(null);
  const [editingBet, setEditingBet] = useState(null);
  const [editOption, setEditOption] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  const userBets = useMemo(() => {
    const ub = bets.filter(isMyBet);
    if (filter === 'all') return ub;
    return ub.filter((b) => b.status === filter);
  }, [bets, isMyBet, filter]);

  const summary = useMemo(() => {
    const all = bets.filter(isMyBet);
    const pending = all.filter((b) => b.status === 'pending');
    const won = all.filter((b) => b.status === 'won');
    const lost = all.filter((b) => b.status === 'lost');
    return {
      total: all.length,
      pending: pending.length,
      won: won.length,
      lost: lost.length,
      totalStaked: pending.reduce((s, b) => s + b.amount, 0),
      totalPotential: pending.reduce((s, b) => s + b.potentialWin, 0),
    };
  }, [bets, isMyBet]);

  // Group bets by event > round
  const groupedBets = useMemo(() => {
    const groups = [];
    // Build a map: marketId -> roundId
    const marketToRound = {};
    markets.forEach((m) => { marketToRound[m.id] = m.roundId; });

    // Build a map: roundId -> { round, eventId }
    const roundMap = {};
    rounds.forEach((r) => { roundMap[r.id] = r; });

    // Build a map: eventId -> event
    const eventMap = {};
    events.forEach((e) => { eventMap[e.id] = e; });

    // Group bets
    const eventGroups = {};
    userBets.forEach((bet) => {
      const roundId = marketToRound[bet.marketId];
      const round = roundId ? roundMap[roundId] : null;
      const eventId = round?.eventId || 'unknown';
      const event = eventId !== 'unknown' ? eventMap[eventId] : null;
      const key = `${eventId}__${roundId || 'unknown'}`;

      if (!eventGroups[eventId]) {
        eventGroups[eventId] = { event, roundGroups: {} };
      }
      if (!eventGroups[eventId].roundGroups[key]) {
        eventGroups[eventId].roundGroups[key] = { round, bets: [] };
      }
      eventGroups[eventId].roundGroups[key].bets.push(bet);
    });

    // Convert to array sorted by event then round number
    Object.entries(eventGroups).forEach(([eventId, { event, roundGroups }]) => {
      const roundEntries = Object.values(roundGroups).sort((a, b) => {
        return (a.round?.roundNumber || 0) - (b.round?.roundNumber || 0);
      });
      groups.push({ event, rounds: roundEntries });
    });

    return groups;
  }, [userBets, markets, rounds, events]);

  const handleCancel = async (betId) => {
    setCancelling(betId);
    try { await cancelBet(betId); } catch (err) { alert(err.message); }
    finally { setCancelling(null); }
  };

  const handleStartEdit = (bet, market) => {
    setEditingBet({ bet, market });
    setEditOption(bet.optionName);
    setEditAmount(String(bet.amount));
    setEditError(''); setEditSuccess('');
  };

  const handleSaveEdit = async () => {
    if (!editingBet) return;
    const newAmount = parseFloat(editAmount);
    if (isNaN(newAmount) || newAmount <= 0) { setEditError('Enter a valid amount'); return; }
    try {
      await replaceBet(editingBet.bet.id, editOption, newAmount);
      setEditSuccess('Bet updated!');
      setTimeout(() => { setEditingBet(null); setEditSuccess(''); }, 1500);
    } catch (err) { setEditError(err.message); }
  };

  return (
    <div className="mybets-page">
      <div className="mybets-header">
        <h2>My Bets</h2>
        <p className="mybets-header-sub">Overview of all your bets across events and rounds</p>
      </div>

      <div className="mybets-summary">
        <div className="mybets-summary-card"><span className="mybets-summary-value">{summary.pending}</span><span className="mybets-summary-label">Active</span></div>
        <div className="mybets-summary-card"><span className="mybets-summary-value">{summary.totalStaked.toLocaleString()}</span><span className="mybets-summary-label">Staked</span></div>
        <div className="mybets-summary-card mybets-summary-card--highlight"><span className="mybets-summary-value">+{summary.totalPotential.toLocaleString()}</span><span className="mybets-summary-label">Potential</span></div>
        <div className="mybets-summary-card mybets-summary-card--won"><span className="mybets-summary-value">{summary.won}</span><span className="mybets-summary-label">Won</span></div>
        <div className="mybets-summary-card mybets-summary-card--lost"><span className="mybets-summary-value">{summary.lost}</span><span className="mybets-summary-label">Lost</span></div>
      </div>

      <div className="mybets-filters">
        {[
          { key: 'all', label: `All (${bets.filter(isMyBet).length})` },
          { key: 'pending', label: `Active (${summary.pending})` },
          { key: 'won', label: `Won (${summary.won})` },
          { key: 'lost', label: `Lost (${summary.lost})` },
        ].map((f) => (
          <button key={f.key} className={`mybets-filter-btn ${filter === f.key ? 'mybets-filter-btn--active' : ''}`} onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
      </div>

      {/* Edit modal */}
      {editingBet && (
        <div className="mybets-edit-overlay" onClick={() => setEditingBet(null)}>
          <div className="mybets-edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Bet</h3>
            <p className="mybets-edit-match">{editingBet.market.matchName}</p>
            <div className="mybets-edit-options">
              {editingBet.market.options.map((opt) => (
                <button key={opt.name} className={`mybets-edit-option ${editOption === opt.name ? 'mybets-edit-option--selected' : ''}`} onClick={() => { setEditOption(opt.name); setEditError(''); }}>
                  <span>{opt.name}</span><strong>{opt.odds}</strong>
                </button>
              ))}
            </div>
            <div className="mybets-edit-amount">
              <label>Stake (coins)</label>
              <input type="number" value={editAmount} onChange={(e) => { setEditAmount(e.target.value); setEditError(''); }} min="1" />
            </div>
            {editAmount && editOption && (
              <div className="mybets-edit-potential">
                Potential Win: <strong>{(parseFloat(editAmount) * (editingBet.market.options.find((o) => o.name === editOption)?.odds || 0)).toFixed(0)}</strong> coins
              </div>
            )}
            {editError && <div className="mybets-edit-error">{editError}</div>}
            {editSuccess && <div className="mybets-edit-success">{editSuccess}</div>}
            <div className="mybets-edit-buttons">
              <button className="mybets-edit-save" onClick={handleSaveEdit}>Save Changes</button>
              <button className="mybets-edit-cancel" onClick={() => setEditingBet(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Grouped bets */}
      {userBets.length === 0 ? (
        <div className="mybets-empty">
          <span className="mybets-empty-icon">🎫</span>
          <p>{filter === 'all' ? 'No bets placed yet. Head to the betting page!' : `No ${filter} bets.`}</p>
        </div>
      ) : (
        <div className="mybets-grouped">
          {groupedBets.map((group, gi) => (
            <div key={gi} className="mybets-event-group">
              <div className="mybets-event-header">
                <span className="mybets-event-icon">📅</span>
                <span className="mybets-event-name">{group.event?.name || 'Unknown Event'}</span>
                {group.event?.status === 'concluded' && <span className="mybets-event-concluded">Concluded</span>}
              </div>

              {group.rounds.map((rndGroup, ri) => {
                const roundStatusClass = rndGroup.round ? `mybets-round-header--${rndGroup.round.status}` : '';
                return (
                  <div key={ri} className="mybets-round-group">
                    <div className={`mybets-round-header ${roundStatusClass}`}>
                      <span className="mybets-round-badge">R{rndGroup.round?.roundNumber || '?'}</span>
                      <span className="mybets-round-name">{rndGroup.round?.name || 'Unknown Round'}</span>
                      <span className="mybets-round-status">{rndGroup.round?.status}</span>
                      <span className="mybets-round-count">{rndGroup.bets.length} bet{rndGroup.bets.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="mybets-list">
                      {rndGroup.bets.map((bet) => {
                        const market = markets.find((m) => m.id === bet.marketId);
                        return (
                          <BetRow key={bet.id} bet={bet} market={market} canModify={settings.allowBetReplacement} onEdit={handleStartEdit} onCancel={handleCancel} cancelling={cancelling} />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
